import { DialogContent, CircularProgress, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import {
  usePutBillFormState,
  usePutBillHandler
} from '~/modules/billing-invoicing/bill/hooks';
import { isNullOrUndefined } from '~/modules/common/util';
import {
  useIsBillDataReflectedOnAvailableToBill,
  useNextReferenceNumberText
} from '~/modules/billing-invoicing/common/hooks';
import { useHasFeatureFlag } from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me';
import { billingTransactionType } from '~/modules/billing-invoicing/common/enums';
import { ProgressContent } from '~/modules/billing-invoicing/common/components';
import BillInfo from '../../bill/components/BillDetails/BillInfo';
import AddLineItemsDialogContent from './AddLineItemsDialogContent';
import AddLineItemsDialogFooter from './AddLineItemsDialogFooter';
import BillingItemGroup from './BillingItemGroup';
import { useLineItemsDialogContext } from './AddLineItemsDialogContext';
import {
  useFilterBasedOnSearchCriteria,
  useCreateInvoiceItemsFromBillItems,
  useAddLineItemsHooks,
  useCreateInvoiceFromBillItemsBatch,
  useCreateBillFromBillingItemsBatchResults,
  useBillingDefaults
} from './hooks';
import getFacets from './facets';
import Toolbar from './Toolbar';
import { useStyles } from './useStyles';

const useProgressStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    justifyContent: 'center',
    minHeight: '415px'
  },
  infoMessage: {
    marginTop: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium
  }
}));

export const AddLineItemsTab = ({
  billId,
  onClose,
  client,
  createBill,
  billCurrency,
  projectUri,
  summarizeColumnOptions,
  setSummarizeColumOptions,
  billingBatchState,
  setBillingBatchState,
  availableToBillProject,
  projectName,
  refetchAvailableToBillRows,
  refetchAvailableToBillTotal,
  refetchFilteredTotals,
  startDate,
  endDate,
  billDetails
}) => {
  const me = useMeContext();
  const classes = useStyles();
  const progressClasses = useProgressStyles();
  const history = useHistory();
  const { params } = useRouteMatch();

  const { nextReferenceNumberText } = useNextReferenceNumberText(
    billingTransactionType.BILL
  );

  const isPsaPrpFixBillingDefaultsEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPrpFixBillingDefaultsEnabled'
  });

  const {
    clientDefaultData,
    invoiceDefaultTemplate,
    billingDefaults
  } = useBillingDefaults({
    availableToBillProject,
    client,
    projectUri,
    startDate,
    endDate,
    isPsaPrpFixBillingDefaultsEnabled
  });
  const { defaultInvoicePaymentTerm } = clientDefaultData || {};
  const { putBill } = usePutBillHandler();
  const [selectedBillingItems, setSelectedBillingItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [createAndView, setCreateAndView] = useState(false);
  const [
    isBillDataReflectedInAvailableToBillData,
    setIsBillDataReflectedInAvailableToBillData
  ] = useState(false);

  const shouldShowLoading = isBillDataReflectedInAvailableToBillData;

  const {
    values,
    setFieldValue,
    errors,
    validateForm,
    isSubmitting,
    setFieldError
  } = usePutBillFormState({
    projectUri,
    projectName,
    me,
    client,
    clientCurrency: billingDefaults?.invoiceCurrency,
    clientPaymentTerm:
      billingDefaults?.defaultInvoicePaymentTerm ?? defaultInvoicePaymentTerm,
    clientInvoiceTemplate: billingDefaults?.invoiceTemplate,
    clientTaxProfile: billingDefaults?.taxProfile,
    poNumber: !isNullOrUndefined(billingDefaults?.poNumber)
      ? billingDefaults?.poNumber
      : '',
    invoiceDefaultTemplate,
    putBill,
    setSaving,
    period: { startDate, endDate },
    billingAddress: billingDefaults?.billingContactInfo,
    onClose,
    summarizeColumnOptions,
    nextReferenceNumberText,
    billingSettings: {
      billLineItemsBy: billingDefaults?.billLineItemsBy,
      description: billingDefaults?.description,
      internalNotes: billingDefaults?.internalNotes,
      showComments: billingDefaults?.showComments
    },
    isPsaPrpFixBillingDefaultsEnabled
  });
  const setColumns = useCallback(
    col => {
      setFieldValue('summarizeColumnOptions', col);
    },
    [setFieldValue]
  );

  const { searchCriteria } = useLineItemsDialogContext();
  const { billingItemSearch } = useFilterBasedOnSearchCriteria({
    searchCriteria
  });
  const searchFacets = useMemo(
    () => getFacets({ columns: values.summarizeColumnOptions, client }),
    [client, values.summarizeColumnOptions]
  );

  const {
    createInvoiceItemsFromBillingItemsBatch
  } = useCreateInvoiceItemsFromBillItems({ setBillingBatchState });
  const { fetchCalculationStatus } = useIsBillDataReflectedOnAvailableToBill();
  const { fetchBatchResults } = useCreateBillFromBillingItemsBatchResults();

  const addModeResourceKey = createBill
    ? 'addLineItemDialog.createBill'
    : 'addLineItemDialog.addLineItems';

  const {
    createInvoiceFromBillItemsBatch
  } = useCreateInvoiceFromBillItemsBatch({
    setBillingBatchState
  });

  const { onAddLineItems, onBatchComplete2 } = useAddLineItemsHooks({
    billDetails,
    billId,
    billingItemSearch,
    columns: values.summarizeColumnOptions,
    createAndView,
    createBill,
    createInvoiceFromBillItemsBatch,
    createInvoiceItemsFromBillingItemsBatch,
    fetchBatchResults,
    fetchCalculationStatus,
    history,
    onClose,
    params,
    putBill,
    refetchAvailableToBillRows,
    refetchAvailableToBillTotal,
    refetchFilteredTotals,
    selectedBillingItems,
    setBillingBatchState,
    setCreateAndView,
    setFieldError,
    setIsBillDataReflectedInAvailableToBillData,
    setSaving,
    setSummarizeColumOptions,
    validateForm,
    values
  });

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        {billingBatchState?.batchId && billingBatchState?.batchInProgress ? (
          <ProgressContent
            classes={progressClasses}
            labelId={
              createBill || createAndView
                ? 'availableToBillList.creatingBill'
                : 'billingTransactionList.addingLineItems'
            }
            batchId={billingBatchState?.batchId}
            onBatchComplete={onBatchComplete2}
            queryType="billing_batch_status"
          />
        ) : (
          <>
            {shouldShowLoading ? (
              <div className={classes.refreshingBillingDataContainer}>
                <CircularProgress size={24} />
                <Typography variant="caption">
                  <FormattedMessage id="availableToBillList.refreshingBillingData" />
                </Typography>
              </div>
            ) : (
              <>
                {Boolean(createBill) && (
                  <BillInfo
                    hasClient
                    isCollapsible
                    containerClass={classes.container}
                    fieldContainerClass={classes.fieldContainer}
                    currencyEditable
                    setFieldValue={setFieldValue}
                    errors={errors}
                    isSubmitting={isSubmitting}
                    values={values}
                  />
                )}
                <Toolbar
                  searchFacets={searchFacets}
                  columns={values.summarizeColumnOptions}
                  createBill={createBill}
                />
                <div className={classes.groupBar}>
                  <BillingItemGroup
                    columns={values.summarizeColumnOptions}
                    setColumns={setColumns}
                  />
                </div>
                <div className={classes.resultContainer}>
                  <AddLineItemsDialogContent
                    classes={classes}
                    billingItemSearch={billingItemSearch}
                    client={client}
                    columns={values.summarizeColumnOptions}
                    setSelectedBillingItems={setSelectedBillingItems}
                    billCurrency={
                      !createBill ? billCurrency : values.billCurrency
                    }
                    projectUri={projectUri}
                  />
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
      <AddLineItemsDialogFooter
        onAddLineItems={onAddLineItems}
        onClose={onClose}
        disableAdd={Boolean(
          saving ||
            createAndView ||
            !selectedBillingItems.any ||
            billingBatchState?.batchInProgress
        )}
        addButtonLabel={addModeResourceKey}
        saveInProgress={saving}
        createAndViewInProgress={createAndView}
        billingBatchState={billingBatchState}
        createBill={createBill}
      />
    </>
  );
};

AddLineItemsTab.propTypes = {
  billId: PropTypes.string,
  billCurrency: PropTypes.object,
  onClose: PropTypes.func,
  client: PropTypes.object,
  createBill: PropTypes.bool,
  availableToBillProject: PropTypes.object,
  projectUri: PropTypes.string,
  summarizeColumnOptions: PropTypes.array,
  setSummarizeColumOptions: PropTypes.func,
  billingBatchState: PropTypes.object,
  setBillingBatchState: PropTypes.func,
  projectName: PropTypes.string,
  refetchAvailableToBillRows: PropTypes.func,
  refetchAvailableToBillTotal: PropTypes.func,
  refetchFilteredTotals: PropTypes.func,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  billDetails: PropTypes.object.isRequired
};

export default AddLineItemsTab;
